import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faWrench } from '@fortawesome/free-solid-svg-icons';
import type { StaffRequestThread } from '@/services/request-thread.service';
import { ThreadTimeline } from './ThreadTimeline';
import { ThreadActions, hasAnyAction } from '../actions/ThreadActions';

/**
 * The two regions C-iii reserves and later sub-stages fill.
 *
 * They are real components with real boundaries, not markup comments, so that
 * C-iv and C-v each replace ONE body without touching the page's layout,
 * data-flow or props. Each already receives everything its sub-stage needs.
 */

function Region({
  title,
  icon,
  count,
  children,
  testId,
}: {
  title: string;
  icon: typeof faWrench;
  count?: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <section
      aria-label={title}
      data-testid={testId}
      className="rounded-xl border border-border bg-card"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {count && <span className="ml-auto text-xs text-muted-foreground">{count}</span>}
      </div>
      <div className="px-4 py-4">{children}</div>
    </section>
  );
}

/**
 * The request's history.
 *
 * The entries are ALREADY here — the composed read brought them, in
 * `createdAt ASC`, and already filtered by the server to what this caller may
 * see. This region fetches nothing and filters nothing: an entry the caller may
 * not see never left the database. See `ThreadTimeline` for why that stays true.
 */
export function ThreadTimelineRegion({ thread }: { thread: StaffRequestThread }) {
  const count = thread.timeline.length;
  return (
    <Region
      title="History"
      icon={faClockRotateLeft}
      count={count === 1 ? '1 entry' : `${count} entries`}
      testId="thread-timeline-region"
    >
      <ThreadTimeline entries={thread.timeline} />
    </Region>
  );
}

/**
 * The actions this caller may take.
 *
 * Everything shown is decided by the SERVER. `capabilities` is computed from
 * permission ∧ state by probing the state machine itself, so nothing here is
 * derived from a role name, a department, `assignedUserId`, `currentOwner`,
 * `waitingFor`, or the workflow state on its own — the last being the subtle
 * one, since state without permission would offer an Operations user the
 * Commercial close button.
 *
 * When there is nothing to do the reason is stated rather than the panel left
 * blank, and no disabled placeholder control is rendered: a greyed-out button
 * for an action the caller can never take is an invitation, not information.
 */
export function ThreadActionRegion({ thread }: { thread: StaffRequestThread }) {
  return (
    <Region title="Actions" icon={faWrench} testId="thread-action-region">
      {hasAnyAction(thread) ? (
        <>
          {thread.capabilities.actionRequired && (
            <p className="mb-3 text-sm text-muted-foreground" data-testid="thread-actions-intro">
              This request is waiting on you.
            </p>
          )}
          <ThreadActions thread={thread} />
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="thread-no-actions">
          {thread.closedAt
            ? 'This request is closed. There is nothing further to do.'
            : 'You have no actions on this request at the moment.'}
        </p>
      )}
    </Region>
  );
}
